import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { ViewToggle, ViewMode } from "@/components/common/ViewToggle";
import { useTranslation } from "react-i18next";

export type SortField = "name" | "dateCreated" | "matchScore";
export type SortOrder = "asc" | "desc";
export type { ViewMode }; // Changed to export type instead of just export

interface CandidatesViewOptionsProps {
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

export const CandidatesViewOptions = ({
  sortField,
  setSortField,
  sortOrder,
  setSortOrder,
  viewMode,
  setViewMode
}: CandidatesViewOptionsProps) => {
  const { t } = useTranslation();
  const getSortLabel = () => {
    switch (sortField) {
      case "name": return t("sortBar.name");
      case "dateCreated": return t("sortBar.dateCreated");
      case "matchScore": return t("sortBar.matchScore");
      default: return t("sortBar.sortBy");
    }
  };

  const handleToggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  return (
    <div className="flex justify-between items-center mb-4 bg-white p-3 rounded-lg shadow-sm">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-500">{t("sortBar.sortBy")}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              {getSortLabel()}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => setSortField("name")}>{t("sortBar.name")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortField("dateCreated")}>{t("sortBar.dateCreated")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortField("matchScore")}>{t("sortBar.matchScore")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleToggleSortOrder}
          className="ml-1"
        >
          {sortOrder === "asc" ? t("sortBar.ascending") : t("sortBar.descending")}
        </Button>
      </div>
      
      <ViewToggle 
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        label={t("sortBar.view")}
      />
    </div>
  );
};
